/**
 * Cart and order placement.
 *
 * Two behaviours here carry real weight:
 *
 *   * **Totals come from the server, never from the client.** The figure the
 *     retailer confirms is computed by the same code that writes the invoice,
 *     so the two can never disagree by a paisa.
 *
 *   * **A credit-limit breach is a warning, not a wall.** The server returns
 *     409 with the exposure figures; the app shows them and offers to proceed.
 *     Only staff can override — and the override is logged and notified to
 *     admin, which is what makes it safe to allow.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Page } from '../../components/Shell';
import {
  Banner, Button, Card, ConfirmSheet, Empty, Money, Select, Sheet,
  Switch, TextArea, TextInput, useToast,
} from '../../components/ui';
import { PaymentCard, type PaymentState } from '../../components/PaymentCard';
import { ReminderSheet } from '../../components/ReminderSheet';
import { useCart, keyOf, rateOf, type CartLine } from '../../lib/cart';
import { useAuth } from '../../lib/auth';
import { useT } from '../../lib/i18n';
import { api, ApiError, mediaUrl } from '../../lib/api';
import { useQuery } from '../../lib/useApi';
import { useOnline } from '../../lib/useApi';
import * as outbox from '../../lib/outbox';
import { businessDate, money } from '../../lib/format';

/** What `POST /api/orders/preview` returns — the invoice's arithmetic, unissued. */
interface Quote {
  pricesIncludeTax: boolean;
  grossAmountPaise: number;
  discountAmountPaise: number;
  taxableAmountPaise: number;
  cgstAmountPaise: number;
  sgstAmountPaise: number;
  igstAmountPaise: number;
  cessAmountPaise: number;
  payableAmountPaise: number;
}

interface Shop {
  id: string;
  shop_name: string;
  shop_code: string;
  is_primary: number;
  address_line1: string;
  outstanding_paise: number;
  beat_id: string | null;
  beat_name: string | null;
}

interface OrderResult {
  orderId: string;
  orderNumber: string;
  invoiceId: string;
  invoiceNumber: string;
  invoiceType: string;
  isProvisional: boolean;
  totals: {
    grossAmountPaise: number;
    discountAmountPaise: number;
    taxableAmountPaise: number;
    cgstAmountPaise: number;
    sgstAmountPaise: number;
    igstAmountPaise: number;
    cessAmountPaise: number;
    roundOffPaise: number;
    netAmountPaise: number;
    display: string;
  };
  creditLimitOverridden: boolean;
  classificationWarning?: string | null;
  pdfUrl: string;
  /**
   * Whether Business Central holds the invoice, and the sentence to show.
   *
   * The server has returned both of these since 18 Sep and NOTHING displayed
   * them — so an order creation waited up to fifteen seconds for an answer
   * that was then dropped on the floor. Optional because an order placed
   * offline through the outbox has no such answer to give.
   */
  sentToBc?: boolean;
  bcNote?: string;
}

/** Short role name for the "Booked by" picker — the staff code already carries the detail. */
function roleLabel(role: string): string {
  switch (role) {
    case 'owner_admin': return 'Admin';
    case 'salesperson': return 'Salesman';
    case 'driver': return 'Driver';
    default: return role;
  }
}

export default function Cart() {
  const t = useT();
  const navigate = useNavigate();
  const toast = useToast();
  const online = useOnline();
  const { user, isStaff } = useAuth();
  // A combined driver-cum-salesperson books AND delivers the order themselves,
  // so the two pickers collapse to one read-only line naming them.
  const isFieldAgent = user?.role === 'field_agent';
  const cart = useCart();

  const [deliveryDate, setDeliveryDate] = useState(() => {
    // Tomorrow by default — same-day is rarely serviceable.
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return businessDate(d);
  });
  const [slot, setSlot] = useState('');
  /**
   * The retailer is standing at the godown and taking the cartons with them.
   *
   * Client decision, 20 Sep. The order is recorded as delivered on the spot,
   * so there is no dispatch, no van, no delivery date and no proof of
   * delivery to collect — and the invoice reaches Business Central by exactly
   * the same path as any other.
   *
   * Staff only. A retailer ordering from their own phone is not at the counter.
   */
  const [counterSale, setCounterSale] = useState(false);
  const [godownId, setGodownId] = useState('');
  const [bookedFor, setBookedFor] = useState('');
  /**
   * Who delivers the order. 'driver' routes it to the beat's driver working
   * today (that routing lands in a later change); 'admin' hands it to Admin to
   * schedule. Defaults to 'driver' — the everyday same-day case. Not sent for a
   * counter sale, which has already left with the customer.
   */
  const [deliveredBy, setDeliveredBy] = useState<'driver' | 'admin'>('driver');
  /**
   * Who hands a COUNTER SALE over at the godown — the godown counter or admin.
   * A counter sale has no driver, so this replaces "Delivered by" when the
   * counter-sale switch is on. Defaults to the counter, the everyday case.
   */
  const [counterHandover, setCounterHandover] = useState<'godown' | 'admin'>('godown');
  // Payment is no longer captured in the cart — it is handled at delivery — so
  // there is no payment mode, tender split or settlement here. Every order
  // books as cash on delivery (see the payload below).
  const [remarks, setRemarks] = useState('');

  /**
   * Counter-sale payment. The money is taken at the godown as the goods leave,
   * so — unlike a delivery order, which settles later at the door — the payment
   * card appears here. Empty until the user enters anything; only sent on a
   * counter sale (see the payload).
   */
  const [payment, setPayment] = useState<PaymentState>({
    discountPaise: 0, discountReason: '', cashPaise: 0, upiPaise: 0,
  });
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [creditWarning, setCreditWarning] = useState<{ limit: number; exposure: number } | null>(null);
  const [placed, setPlaced] = useState<OrderResult | null>(null);
  /**
   * WHO THE ORDER WAS FOR, CAPTURED BEFORE THE CART IS CLEARED.
   *
   * `clearCustomer()` runs the instant the order succeeds — it has to, or the
   * NEXT shop's order silently attaches to this retailer. But the confirmation
   * screen is rendered afterwards and needs the customer to offer "Back to
   * customer", so reading `cart.customerId` there always found null and the
   * button never appeared for an on-behalf order: the only case it exists for.
   *
   * Found by placing a real order on 3 Oct, not by any test — the button is
   * rendered from state that only a live round trip produces.
   */
  const [placedForCustomerId, setPlacedForCustomerId] = useState<string | null>(null);

  // Only fetched for staff, and only the two lists the two selectors need.
  const godowns = useQuery<{ items: { id: string; code: string; name: string; is_default: number }[] }>(
    isStaff ? '/api/admin/masters/godowns-for-sale' : null,
  );
  /**
   * EVERY active employee an order can be booked by — admin, drivers and
   * salesmen alike. Replaces the salespersons-only list: the client takes
   * orders from whoever is free, and "Booked by" is now a required field, so it
   * must offer all of them. The value carried is the USER id.
   */
  const employees = useQuery<{ items: { userId: string; name: string; role: string; code: string | null }[] }>(
    isStaff ? '/api/admin/masters/employees-for-order' : null,
  );

  /**
   * One godown means there is nothing to choose.
   *
   * Pre-selected rather than shown as a dropdown with a single option, which
   * is a decision disguised as a question. The field appears when the client
   * actually has two or three, which is the case this was asked for.
   */
  useEffect(() => {
    const items = godowns.data?.items ?? [];
    if (!godownId && items.length > 0) {
      setGodownId((items.find((g) => g.is_default === 1) ?? items[0]!).id);
    }
  }, [godowns.data, godownId]);

  /**
   * "Booked by" is required and pre-fills the signed-in employee — the common
   * case is that whoever is holding the phone booked the order. Set once the
   * employee list has loaded and confirmed the signed-in user is in it, so the
   * default is always a valid option; they can change it. Staff only.
   */
  useEffect(() => {
    if (!isStaff || bookedFor) return;
    // A field agent books as themselves — set it straight away, don't wait on
    // the employee list, so the read-only line and the Place button are ready.
    if (isFieldAgent && user?.id) { setBookedFor(user.id); return; }
    const me = employees.data?.items.find((e) => e.userId === user?.id);
    if (me) setBookedFor(me.userId);
  }, [employees.data, isStaff, isFieldAgent, bookedFor, user?.id]);

  /**
   * Boxes and pieces, counted and valued apart.
   *
   * `rateOf` is the shared helper, so this estimate uses the same box-price
   * fallback the server will — a figure on screen that disagrees with the
   * invoice behind it is worse than no figure.
   */
  const boxLines = cart.lines.filter((l) => (l.soldUom ?? 'PCS') === 'BOX');
  const pcsLines = cart.lines.filter((l) => (l.soldUom ?? 'PCS') !== 'BOX');
  const boxCount = boxLines.reduce((n, l) => n + l.quantity, 0);
  const pcsCount = pcsLines.reduce((n, l) => n + l.quantity, 0);
  const boxGross = boxLines.reduce((n, l) => n + l.quantity * (l.typedPricePaise ?? rateOf(l)), 0);
  const pcsGross = pcsLines.reduce((n, l) => n + l.quantity * (l.typedPricePaise ?? rateOf(l)), 0);

  /**
   * HOW MANY — SAID WITHOUT ADDING CARTONS TO BOTTLES.
   *
   * `cart.totalQuantity` is a plain sum, so a basket of 3 boxes and 4 loose
   * bottles reported "7 qty". That number is not wrong so much as meaningless,
   * and it is meaningless in the one direction this project treats as
   * dangerous: it reads as seven items when twenty-seven bottles are leaving
   * the godown. It appeared on the action bar and, worse, on the confirmation
   * sheet — the last screen before somebody commits.
   */
  const quantityLabel = boxCount > 0 && pcsCount > 0
    ? `${boxCount} Pets + ${pcsCount} Pieces`
    : boxCount > 0
      ? `${boxCount} Pets`
      : `${pcsCount} Pieces`;

  /**
   * The customer being ordered for — their own id, or the one a staff member
   * picked. See the note in `Catalogue`: reading only `user.customerId` meant
   * this screen fetched no shops for any staff role, which left `cart.shopId`
   * unset and the place button disabled for ever.
   */
  const orderingFor = user?.customerId ?? cart.customerId;
  const onBehalf = !user?.customerId && !!cart.customerId;

  /**
   * The server's own arithmetic for this basket, fetched as it changes.
   *
   * DEBOUNCED, because every tap on a stepper changes the basket and a
   * request per tap would both hammer the worker and arrive out of order —
   * the total shown would be whichever response happened to land last.
   *
   * The settlement below is measured against `quote.payableAmountPaise` when
   * it has arrived, because that is the number the customer actually owes.
   * Before it arrives the gross estimate stands in: a balance that is briefly
   * approximate is better than a blank where the balance goes.
   */
  const [quote, setQuote] = useState<Quote | null>(null);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const basketKey = JSON.stringify(
    cart.lines.map((l) => [l.productId, l.quantity, l.soldUom ?? 'PCS', l.typedPricePaise]),
  );

  useEffect(() => {
    if (!cart.shopId || cart.lines.length === 0) { setQuote(null); return undefined; }
    let cancelled = false;
    setQuoteError(null);
    const timer = window.setTimeout(() => {
      api.post<Quote>('/api/orders/preview', {
        ...(orderingFor ? { customerId: orderingFor } : {}),
        customerShopId: cart.shopId,
        lines: cart.lines.map((l) => ({
          productId: l.productId,
          quantity: l.quantity,
          soldUom: l.soldUom ?? 'PCS',
          ...(l.typedPricePaise !== undefined
            ? { unitPricePaiseOverride: l.typedPricePaise }
            : {}),
        })),
      })
        .then((q) => { if (!cancelled) setQuote(q); })
        .catch((e) => {
          if (cancelled) return;
          /**
           * The estimate stays on screen and the reason is shown. Replacing a
           * real number with "something went wrong" would leave the person
           * with no total at all, which is the state this replaced.
           */
          setQuote(null);
          setQuoteError(e instanceof Error ? e.message : 'Could not work out the tax');
        });
    }, 400);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [basketKey, cart.shopId, orderingFor]);


  // `/customers/me/shops` is scoped to the token, so it only works for a
  // customer ordering for themselves. Staff read the chosen customer's shops.
  const { data: shops } = useQuery<{ shops: Shop[] }>(
    user?.customerId
      ? '/api/customers/me/shops'
      : cart.customerId ? `/api/customers/${cart.customerId}/shops` : null,
    { cacheKey: `shops:${orderingFor ?? 'none'}`, ttlSeconds: 86400 },
  );

  // Pick the primary shop automatically so a single-shop retailer never sees a
  // selector they do not need.
  useEffect(() => {
    if (!shops?.shops.length || cart.shopId) return;
    const primary = shops.shops.find((s) => s.is_primary === 1) ?? shops.shops[0];
    if (primary) cart.setShop(primary.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shops]);

  const selectedShop = useMemo(
    () => shops?.shops.find((s) => s.id === cart.shopId) ?? null,
    [shops, cart.shopId],
  );

  async function place(overrideCredit = false) {
    if (!cart.shopId || cart.lines.length === 0) return;

    // A discount needs a reason — caught here so the person is not bounced by
    // the server after confirming. The server enforces the same rule.
    if (counterSale && payment.discountPaise > 0 && !payment.discountReason.trim()) {
      toast.show('Add a reason for the discount.', 'danger');
      return;
    }
    setBusy(true);
    setCreditWarning(null);

    const payload = {
      // Omitted when the caller IS the customer: the server takes it from the
      // token in that case, and must, because a customer may never order for
      // anyone else.
      customerId: orderingFor ?? undefined,
      customerShopId: cart.shopId,
      lines: cart.lines.map((l) => ({
        productId: l.productId,
        quantity: l.quantity,
        // The unit. Without it a basket of five CASES reaches the server as
        // five bottles — priced, invoiced and taken off stock as five bottles.
        soldUom: l.soldUom ?? 'PCS',
        // Only when one was typed. Sending the catalogue figure back as an
        // "override" would turn every order into a priced-by-hand order in
        // the audit trail, and the trail would stop meaning anything.
        ...(l.typedPricePaise !== undefined
          ? { unitPricePaiseOverride: l.typedPricePaise }
          : {}),
      })),
      // No requested delivery on a counter sale — the goods left with them.
      // Sending today's date would put a delivery expectation on a record that
      // is already complete.
      requestedDeliveryDate: counterSale ? undefined : deliveryDate,
      requestedDeliverySlot: counterSale ? undefined : (slot || undefined),
      // Payment is handled at delivery, not at ordering — the cart no longer
      // captures a tender, so every order books as cash on delivery and sends
      // no settlement block.
      paymentMode: 'cod',
      remarks: remarks.trim() || undefined,
      ...(overrideCredit ? { overrideCreditLimit: true } : {}),
      ...(counterSale ? { isCounterSale: true } : {}),
      // Which godown the cartons were carried out of. Only on a counter sale;
      // nothing else has one, and sending it anyway would record a location
      // for goods that are still in the warehouse.
      ...(counterSale && godownId ? { godownId } : {}),
      // The money taken at the godown. Only on a counter sale; a delivery order
      // carries no tender (it settles later at the door). cash + upi over the
      // bill is kept by the server as an advance on the invoice.
      ...(counterSale ? {
        settlement: {
          cashPaise: payment.cashPaise,
          upiPaise: payment.upiPaise,
          discountPaise: payment.discountPaise,
          ...(payment.discountReason.trim() ? { discountReason: payment.discountReason.trim() } : {}),
        },
      } : {}),
      /**
       * Whose book this order counts towards.
       *
       * Sent only when somebody actually picked, so the server can tell a
       * CHOSEN attribution from an inferred one — "the salesperson picked
       * himself" and "the app assumed the salesperson" look identical
       * afterwards and mean different things in a commission dispute.
       */
      // Who booked it — a user id, any active employee. The server records it
      // and fills the commission column from it when the booker is a salesman.
      ...(isStaff && bookedFor ? { bookedByUserId: bookedFor } : {}),
      // Who delivers it. 'driver' routes to the customer's beat; 'admin'
      // schedules. Only on a delivery order.
      ...(isStaff && !counterSale ? { deliveredBy } : {}),
      // Who handed a counter sale over at the godown. Only on a counter sale.
      ...(isStaff && counterSale ? { counterHandover } : {}),
    };

    // Offline: queue it. The order gets its final invoice number from the
    // device's pre-reserved block on the server side once it syncs, and the
    // client UUID makes a replay safe.
    if (!online) {
      await outbox.enqueue({
        method: 'POST',
        path: '/api/orders',
        body: payload,
        label: `${t('order.number')} — ${selectedShop?.shop_name ?? ''}`,
        kind: 'order',
      });
      if (onBehalf) cart.clearCustomer(); else cart.clear();
      setBusy(false);
      toast.show(t('app.offlineHint'), 'ok');
      navigate('/orders');
      return;
    }

    try {
      const result = await api.post<OrderResult & { duplicate?: boolean }>('/api/orders', payload);
      // Already placed (a double-tap or an offline replay). Say so plainly and
      // open the existing order rather than showing a second confirmation. #3.
      if (result.duplicate) {
        toast.show(`Already placed — this order was received as ${result.orderNumber}.`, 'ok');
        if (onBehalf) cart.clearCustomer(); else cart.clear();
        navigate(result.orderId ? `/orders/${result.orderId}` : '/orders');
        return;
      }
      // Captured first: the next line is what makes it unreadable.
      setPlacedForCustomerId(orderingFor ?? null);
      // On an on-behalf order, release the customer too. Leaving them selected
      // would silently attach the NEXT shop's order to the previous retailer.
      if (onBehalf) cart.clearCustomer(); else cart.clear();
      setPlaced(result);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'CREDIT_LIMIT_EXCEEDED') {
        // The server hands back the numbers so the UI can show the real
        // exposure rather than a vague "limit exceeded".
        setCreditWarning({
          limit: Number(err.fields?.creditLimitPaise ?? 0),
          exposure: Number(err.fields?.currentExposurePaise ?? 0),
        });
      } else if (err instanceof ApiError) {
        toast.show(err.message, 'danger');
      } else {
        toast.show(t('app.somethingWrong'), 'danger');
      }
    } finally {
      setBusy(false);
      setConfirmOpen(false);
    }
  }

  if (placed) {
    return <OrderPlaced result={placed} customerId={placedForCustomerId} isStaff={isStaff} />;
  }

  if (cart.lines.length === 0) {
    return (
      <Page title={t('cart.title')} badges={{ cart: 0 }}>
        <Empty
          title={t('cart.empty')}
          hint={t('cart.emptyHint')}
          action={<Button onClick={() => navigate('/catalogue')}>{t('nav.catalogue')}</Button>}
        />
      </Page>
    );
  }

  return (
    <Page title={t('cart.title')} back="/catalogue" badges={{ cart: cart.totalQuantity }}>
      <div className="stack">
        {!online && <Banner tone="warn" title={t('app.offline')}>{t('app.offlineHint')}</Banner>}

        {/* Whose order this is, stated plainly and unmissably.
            A staff member working down a beat has one cart and many
            retailers; the cost of getting this wrong is a bill sent to the
            wrong shop, so it is a banner rather than a subtitle. */}
        {onBehalf && (
          <Banner
            tone="info"
            title={`Ordering for ${cart.customerName ?? 'customer'}`}
            action={
              <Button size="sm" variant="ghost" onClick={() => { cart.clearCustomer(); navigate('/customers'); }}>
                Change
              </Button>
            }
          >
            <span className="t-sm">This order will be invoiced to them, not to you.</span>
          </Banner>
        )}

        {/* Shown only when the retailer actually has more than one outlet.
            Switching shop clears the cart — see the note in lib/cart.tsx. */}
        {shops && shops.shops.length > 1 && (
          <Select
            label="Deliver to"
            required
            value={cart.shopId ?? ''}
            options={shops.shops.map((s) => ({ value: s.id, label: `${s.shop_name} · ${s.address_line1}` }))}
            onChange={(e) => cart.setShop(e.target.value)}
          />
        )}

        <Card flush>
          <div className="list">
            {cart.lines.map((line) => (
              <CartRow key={keyOf(line)} line={line} mayPrice={isStaff} />
            ))}
          </div>
        </Card>

        {/* Indicative only. The authoritative breakdown comes back with the
            order response and is shown on the confirmation screen. */}
        <Card>
          {/*
            SEPARATE GROSS FOR BOXES AND FOR PIECES.

            Requested 2 Oct, and it is not decoration: the two are priced
            differently, so a single total hides the one number a salesperson
            checks before committing — whether the carton discount actually
            applied. Shown only when the basket holds both, because a line
            labelled "boxes: ₹0" on a basket of loose bottles is noise.
          */}
          {boxGross > 0 && pcsGross > 0 && (
            <>
              <div className="row-between">
                <span className="t-sm t-muted">Pets ({boxCount})</span>
                <Money paise={boxGross} />
              </div>
              <div className="row-between" style={{ marginTop: 4 }}>
                <span className="t-sm t-muted">Pieces ({pcsCount})</span>
                <Money paise={pcsGross} />
              </div>
              <hr className="divider" />
            </>
          )}
          <div className="row-between">
            <span className="t-sm t-muted">
              {t('cart.gross')}
              {!(boxGross > 0 && pcsGross > 0) && ` (${cart.totalQuantity})`}
            </span>
            <Money paise={cart.estimatedGrossPaise} />
          </div>
          {/*
            THE REAL TOTAL, BEFORE A GST NUMBER IS SPENT.

            This used to say "discount and tax are calculated when you place
            the order", which was true and useless. The only code that knew
            the arithmetic ran inside the POST that also allocates an invoice
            number — and a GST number, once issued, comes back only by credit
            note. So nobody could check a total before committing to it.

            `/api/orders/preview` runs the same priceBasket and sumDocument the
            order does and writes nothing.
          */}
          {quote ? (
            <>
              <hr className="divider" />
              <div className="row-between">
                <span className="t-sm t-muted">{t('cart.taxable')}</span>
                <Money paise={quote.taxableAmountPaise} />
              </div>
              <div className="row-between" style={{ marginTop: 4 }}>
                <span className="t-sm t-muted">
                  GST
                  {/*
                    Said out loud, because "₹650" means two different things
                    depending on the answer and this is the screen where the
                    person has to know which.
                  */}
                  {quote.pricesIncludeTax ? ' (already in the price)' : ' (added on top)'}
                </span>
                <Money paise={quote.cgstAmountPaise + quote.sgstAmountPaise
                  + quote.igstAmountPaise + quote.cessAmountPaise} />
              </div>
              <div className="row-between" style={{ marginTop: 6 }}>
                <span className="t-sm t-semi">{t('cart.payable')}</span>
                <Money paise={quote.payableAmountPaise} className="t-semi" />
              </div>
            </>
          ) : (
            <div className="t-xs t-faint" style={{ marginTop: 6 }}>
              {quoteError ?? 'Working out tax and discount…'}
            </div>
          )}
        </Card>

        <Card>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/*
              COUNTER SALE — staff only, and it removes the delivery fields
              rather than leaving them to be ignored.

              A date field on a sale where the goods have already left with the
              customer is a question with no right answer, and whatever gets
              typed in it becomes a delivery expectation nobody will meet.
            */}
            {isStaff && (
              <Switch
                checked={counterSale}
                onChange={setCounterSale}
                label="Counter sale — collected from the godown"
                hint="No delivery, no van, no POD. The invoice still goes to Business Central."
              />
            )}

            {/*
              WHICH GODOWN THE GOODS LEFT FROM.

              The client runs two or three. Business Central's location code
              was a single global setting, so a carton carried out of the
              second godown reduced the FIRST one's stock in BC — two wrong
              counts, in opposite directions, with nothing in either system
              contradicting itself.
            */}
            {counterSale && (godowns.data?.items.length ?? 0) > 1 && (
              <Select
                label="Collected from"
                required
                value={godownId}
                options={(godowns.data?.items ?? []).map((g) => ({
                  value: g.id, label: `${g.name} (${g.code})`,
                }))}
                onChange={(e) => setGodownId(e.target.value)}
              />
            )}

            {/*
              WHOSE ORDER THIS IS.

              Blank means "whoever is signed in", which is what happens today
              and stays the default — a required picker would make every order
              slower to serve a minority of cases. Choosing somebody records
              that a person decided, which is the part that matters when
              commission is disputed.
            */}
            {/*
              A combined driver-cum-salesperson is both the booker and the
              deliverer, so instead of two pickers they see one read-only line
              naming themselves — "Booked & delivered by <name>" on a delivery,
              "Booked by <name>" on a counter sale (the handover picker stays).
            */}
            {isStaff && isFieldAgent && (
              <div className="field">
                <label>{counterSale ? 'Booked by' : 'Booked & delivered by'}</label>
                <div
                  style={{
                    border: '1px solid var(--line)', borderRadius: 'var(--radius)',
                    padding: '9px 12px', fontSize: 14, background: 'var(--surface-2, #f7f8fa)',
                  }}
                >
                  {user?.fullName ?? 'You'}
                </div>
              </div>
            )}

            {isStaff && !isFieldAgent && (
              <Select
                /**
                 * "Booked BY" — whose book the order counts towards, which is
                 * the person who made the sale. Now REQUIRED and drawn from
                 * every active employee (admin, drivers, salesmen), because the
                 * client takes orders from whoever is free; it pre-fills the
                 * signed-in person. The value is a USER id — the server records
                 * it and, when that user is a salesman, fills the commission
                 * column from it too.
                 */
                label="Booked by"
                required
                value={bookedFor}
                placeholder="Select who booked this"
                options={(employees.data?.items ?? []).map((e) => ({
                  value: e.userId,
                  label: `${e.name}${e.code ? ` (${e.code})` : ''} · ${roleLabel(e.role)}`,
                }))}
                onChange={(e) => setBookedFor(e.target.value)}
              />
            )}

            {/*
              DELIVERED BY — staff delivery orders. REQUIRED.

              'Driver' routes the order to the driver working this shop's beat
              today (the server resolves the beat from the customer and hands it
              to whichever driver has an open day — no driver is picked here).
              'Admin' hands it to Admin to schedule. Replaced on a counter sale
              by "Handed over by" below, which has no driver.
            */}
            {isStaff && !counterSale && !isFieldAgent && (
              <Select
                label="Delivered by"
                required
                value={deliveredBy}
                options={[
                  { value: 'driver', label: 'Driver' },
                  { value: 'admin', label: 'Admin' },
                ]}
                onChange={(e) => setDeliveredBy(e.target.value as 'driver' | 'admin')}
                hint="Driver — delivered on the beat run by the beat's driver. Admin — Admin schedules the dispatch."
              />
            )}

            {/*
              DEF-08 soft warning. A driver-delivered order for a shop with no
              beat cannot auto-route — it waits in the admin dispatch queue. That
              is a valid state (a brand-new shop may not have a beat yet), so this
              is a non-blocking heads-up, not a block: the order still places.
            */}
            {isStaff && !counterSale && (isFieldAgent || deliveredBy === 'driver')
              && selectedShop && !selectedShop.beat_id && (
              <Banner tone="warn" title="This shop has no beat">
                <span className="t-sm">
                  The order can't auto‑route to a driver and will wait in the admin dispatch
                  queue. You can still place it — assign a beat to this shop to route it
                  automatically next time.
                </span>
              </Banner>
            )}

            {/*
              HANDED OVER BY — the counter-sale equivalent of "Delivered by".

              A counter sale leaves with the retailer at the godown, so there is
              no driver and no beat. It is handed over either at the godown
              counter or by Admin. REQUIRED, like the delivery case.
            */}
            {isStaff && counterSale && (
              <Select
                label="Handed over by"
                required
                value={counterHandover}
                options={[
                  { value: 'godown', label: 'Godown' },
                  { value: 'admin', label: 'Admin' },
                ]}
                onChange={(e) => setCounterHandover(e.target.value as 'godown' | 'admin')}
                hint="Godown — given out at the godown counter. Admin — handed over by Admin."
              />
            )}

            {!counterSale && (
              <>
                <TextInput
                  label={t('cart.deliveryDate')}
                  required
                  type="date"
                  min={businessDate()}
                  value={deliveryDate}
                  onChange={(e) => setDeliveryDate(e.target.value)}
                />
                <TextInput
                  label={t('cart.deliverySlot')}
                  placeholder="e.g. morning, after 4pm"
                  value={slot}
                  onChange={(e) => setSlot(e.target.value)}
                />
              </>
            )}

            {/* Payment is captured at delivery, not here — the payment-method
                dropdown and the "Payment taken now" card were removed at the
                client's request. Orders book as cash on delivery. */}

            <TextArea
              label={t('cart.remarks')}
              value={remarks}
              maxLength={500}
              onChange={(e) => setRemarks(e.target.value)}
            />
          </div>
        </Card>

        {/*
          PAYMENT — counter sale only, and only once there is a bill to pay
          against. The retailer is standing at the godown paying as the cartons
          go out, so the money is taken here; a delivery order settles at the
          door on the POD instead. Over-tender is kept as an advance (see the
          card and the server handler).
        */}
        {counterSale && quote && (
          <PaymentCard
            totalPaise={quote.payableAmountPaise}
            value={payment}
            onChange={setPayment}
          />
        )}

        {selectedShop && selectedShop.outstanding_paise > 0 && (
          <Banner tone="info">
            <span className="t-sm">
              {t('ledger.outstanding')}: {money(selectedShop.outstanding_paise)}
            </span>
          </Banner>
        )}
      </div>

      <div className="actionbar">
        <div className="grow" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <span className="t-xs t-muted">{quantityLabel}</span>
          <Money paise={cart.estimatedGrossPaise} className="t-lg" />
        </div>
        <Button
          size="lg"
          busy={busy}
          disabled={!cart.shopId || !deliveryDate || (isStaff && !bookedFor)}
          onClick={() => setConfirmOpen(true)}
        >
          {t('cart.placeOrder')}
        </Button>
      </div>

      <ConfirmSheet
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => void place(false)}
        busy={busy}
        title={t('cart.placeOrder')}
        confirmLabel={t('app.confirm')}
        body={
          <div className="stack-2">
            <p className="t-sm t-muted">
              {/*
                The separator only when there is something after it. An admin
                ordering on behalf has no shop row loaded here, which printed a
                trailing "7 qty · " with nothing following it.
              */}
              {quantityLabel}{selectedShop?.shop_name ? ` · ${selectedShop.shop_name}` : ''}
            </p>
            <p className="t-sm t-muted">
              {t('cart.deliveryDate')}: {deliveryDate}
            </p>
            <Banner tone="info">
              <span className="t-xs">
                Your invoice is generated as soon as the order is placed.
              </span>
            </Banner>
          </div>
        }
      />

      {/* Credit-limit warning. Staff may override; a retailer cannot. */}
      <Sheet
        open={!!creditWarning}
        onClose={() => setCreditWarning(null)}
        title={t('credit.warningTitle')}
        footer={
          isStaff ? (
            <>
              <Button variant="ghost" block onClick={() => setCreditWarning(null)}>
                {t('app.cancel')}
              </Button>
              <Button variant="warn" block busy={busy} onClick={() => void place(true)}>
                {t('credit.override')}
              </Button>
            </>
          ) : (
            <Button variant="ghost" block onClick={() => setCreditWarning(null)}>
              {t('app.close')}
            </Button>
          )
        }
      >
        {creditWarning && (
          <div className="stack-2">
            <p className="t-sm">
              {t('credit.warningBody', {
                projected: money(creditWarning.exposure + cart.estimatedGrossPaise),
                limit: money(creditWarning.limit),
              })}
            </p>
            <div className="row-between t-sm">
              <span className="t-muted">{t('ledger.outstanding')}</span>
              <Money paise={creditWarning.exposure} />
            </div>
            <div className="row-between t-sm">
              <span className="t-muted">{t('ledger.creditLimit')}</span>
              <Money paise={creditWarning.limit} />
            </div>
            {isStaff && (
              <Banner tone="warn">
                <span className="t-xs">{t('credit.overrideNote')}</span>
              </Banner>
            )}
            {!isStaff && (
              <Banner tone="info">
                <span className="t-xs">Please clear some outstanding, or contact us to raise your limit.</span>
              </Banner>
            )}
          </div>
        )}
      </Sheet>
    </Page>
  );
}

/**
 * The unit-price box on a cart line.
 *
 * WHY THIS IS A COMPONENT AND NOT AN `<input>`.
 *
 * It was an input, whose `value` was recomputed from the stored paise on every
 * render as `(paise / 100).toFixed(2)`. So typing `5` stored 500 and the box
 * instantly redrew itself as "5.00" with the caret at the end. The next
 * keystroke appended to a FORMATTED string: "5.000", then "5.0005". A price of
 * 50 could not be typed at all, which is what "price input is unusable" meant.
 *
 * The rule a controlled numeric input has to obey is that the text belongs to
 * the person typing until they stop. So the text lives here, as text, and only
 * the parsed value is published upward. Reformatting happens on blur, when the
 * caret is no longer in the field and cannot be moved out from under anybody.
 *
 * `MoneyInput` in components/ui.tsx already did this correctly; the cart simply
 * never used it, and it renders a full labelled field that does not fit here.
 */
function PriceField({
  valuePaise, onChangePaise, label,
}: {
  valuePaise: number | null;
  onChangePaise: (paise: number | null) => void;
  label: string;
}) {
  const [text, setText] = useState(valuePaise === null ? '' : (valuePaise / 100).toFixed(2));

  // Accept an outside change (cleared by "Use catalogue", a restored draft)
  // WITHOUT clobbering what is being typed: only resync when the value on the
  // outside no longer matches what this text parses to.
  useEffect(() => {
    const parsed = text.trim() === '' ? null : Math.round(parseFloat(text) * 100);
    if (parsed !== valuePaise) {
      setText(valuePaise === null ? '' : (valuePaise / 100).toFixed(2));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valuePaise]);

  return (
    <input
      className="input input--num"
      inputMode="decimal"
      aria-label={label}
      placeholder="Unit price ₹"
      value={text}
      onChange={(e) => {
        // One decimal point, digits either side. Anything else is dropped as it
        // is typed rather than rejected at submit.
        const raw = e.target.value.replace(/[^\d.]/g, '');
        const parts = raw.split('.');
        const clean = parts.length > 2 ? `${parts[0]}.${parts.slice(1).join('')}` : raw;
        setText(clean);
        if (clean.trim() === '') { onChangePaise(null); return; }
        const n = parseFloat(clean);
        onChangePaise(Number.isFinite(n) ? Math.round(n * 100) : null);
      }}
      onBlur={() => {
        // Tidy up only once the field is left: "5" becomes "5.00", "5." becomes
        // "5.00", and an unparseable leftover like "." becomes empty.
        if (text.trim() === '') return;
        const n = parseFloat(text);
        setText(Number.isFinite(n) ? n.toFixed(2) : '');
        if (!Number.isFinite(n)) onChangePaise(null);
      }}
      style={{ maxWidth: 120 }}
    />
  );
}

function CartRow({ line, mayPrice }: { line: CartLine; mayPrice: boolean }) {
  const cart = useCart();
  /**
   * The price that will actually be charged for this line.
   *
   * The typed one when there is one, so the figure on screen is the figure
   * the server will use. Showing the catalogue rate beside a typed price is
   * how somebody signs off a total they never agreed to.
   */
  const effective = line.typedPricePaise ?? rateOf(line);
  const isBox = (line.soldUom ?? 'PCS') === 'BOX';
  // "Pets (24)" for a box line, "Pieces" for a loose one — the client's words
  // for a case and a single unit, with the pack size in brackets.
  const unitLabel = isBox
    ? `Pets${line.packQty ? ` (${line.packQty})` : ''}`
    : 'Pieces';

  /**
   * An item the catalogue has no price for.
   *
   * Said here rather than left to fail at submit: the server refuses such a
   * line, and finding that out after filling a basket in a shop is the worst
   * possible moment.
   */
  const needsPrice = mayPrice && effective <= 0;

  return (
    <div className="list__item list__item--static">
      {line.imageUrl ? (
        <img src={mediaUrl(line.imageUrl)} alt="" className="thumb" loading="lazy" />
      ) : (
        <div className="thumb" aria-hidden />
      )}
      <div className="grow" style={{ minWidth: 0 }}>
        <div className="t-semi t-2line">{line.productName}</div>
        <div className="t-xs t-muted">
          <Money paise={effective} /> / {unitLabel}
          {line.typedPricePaise !== undefined && <> · price entered</>}
        </div>
        {/*
          A box line says what it adds up to in pieces.

          The shopkeeper agreed to a number of cartons; the person checking the
          delivery counts bottles. Both figures on the line is what stops the
          second from being a surprise.
        */}
        {isBox && line.packQty ? (
          <div className="t-xs t-muted">
            {line.quantity} × {line.packQty} = {line.quantity * line.packQty} Pieces
          </div>
        ) : null}
        <div className="t-sm t-semi" style={{ marginTop: 2 }}>
          <Money paise={effective * line.quantity} />
        </div>

        {/*
          MANUAL PRICE ENTRY — STAFF ONLY.

          Client decision, 18 Sep: Business Central holds no selling price for
          this catalogue, so the person raising the order types it. Hidden from
          a retailer entirely, and the server ignores the field for a customer
          regardless of what this screen does — a price a buyer can name is not
          a price.
        */}
        {mayPrice && (
          <div className="row" style={{ gap: 6, alignItems: 'center', marginTop: 4 }}>
            <PriceField
              label={`Price per ${unitLabel} for ${line.productName}`}
              valuePaise={line.typedPricePaise ?? null}
              onChangePaise={(paise) => cart.setPrice(keyOf(line), paise)}
            />
            {line.typedPricePaise !== undefined && (
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => cart.setPrice(keyOf(line), null)}
              >
                Use catalogue
              </button>
            )}
          </div>
        )}

        {needsPrice && (
          <div className="t-xs t-danger" style={{ marginTop: 2 }}>
            No catalogue price — enter one, or this order will be refused.
          </div>
        )}
      </div>
      <div className="stack-1" style={{ gap: 2, alignItems: 'flex-end' }}>
        {/*
          QUANTITY SHOWN, NOT EDITED.

          The client asked to drop the +/- stepper from the cart: a line's
          quantity is set on the catalogue, and the cart is where it is
          reviewed. Stated in the client's words — Pets for a case line,
          Pieces for a loose one.
        */}
        <span className="t-sm t-semi" style={{ whiteSpace: 'nowrap' }}>
          {line.quantity} {isBox ? (line.quantity === 1 ? 'Pet' : 'Pets') : 'Pieces'}
        </span>
      </div>
    </div>
  );
}

/**
 * Confirmation screen.
 *
 * Shows the server-computed breakdown line by line. A retailer who can see
 * exactly how ₹3,600 became ₹4,864 does not phone to ask.
 */
function OrderPlaced({ result, customerId, isStaff }: {
  result: OrderResult;
  customerId: string | null;
  isStaff: boolean;
}) {
  const t = useT();
  const navigate = useNavigate();
  const totals = result.totals;
  const [reminderOpen, setReminderOpen] = useState(false);

  return (
    <Page title={t('order.placed')} hideNav>
      <div className="stack-4">
        <div className="t-center stack-2" style={{ paddingTop: 16 }}>
          <div
            style={{
              width: 56, height: 56, borderRadius: '50%', background: 'var(--ok-bg)',
              color: 'var(--ok)', display: 'grid', placeItems: 'center', margin: '0 auto',
              fontSize: 26, fontWeight: 700,
            }}
            aria-hidden
          >✓</div>
          <h1>{result.orderNumber}</h1>
          <p className="t-sm t-muted">
            {t('order.invoice')} {result.invoiceNumber}
            {result.invoiceType === 'bill_of_supply' && ' · Bill of supply'}
          </p>
        </div>

        {result.isProvisional && (
          <Banner tone="warn">
            Recorded offline. It will be confirmed when this device reconnects.
          </Banner>
        )}
        {result.creditLimitOverridden && (
          <Banner tone="warn">Placed above the credit limit. Our team has been notified.</Banner>
        )}
        {result.classificationWarning && (
          <Banner tone="warn">{result.classificationWarning}</Banner>
        )}
        {/*
          WHAT BUSINESS CENTRAL KNOWS, SAID OUT LOUD.

          Only for staff: a retailer has no idea what Business Central is, and
          a sentence about accounting software on his order confirmation is
          noise. The people who need it are the ones who will be asked why an
          invoice is not in the books.

          `sentToBc === false` rather than `!result.sentToBc`, because an
          offline order omits the field entirely and absent is not the same as
          not sent.
        */}
        {isStaff && result.sentToBc === false && result.bcNote && (
          <Banner tone="warn">{result.bcNote}</Banner>
        )}

        <Card>
          <div className="stack-2">
            <Row label={t('cart.gross')} paise={totals.grossAmountPaise} />
            {totals.discountAmountPaise > 0 && (
              <Row label={t('cart.discount')} paise={-totals.discountAmountPaise} tone="ok" />
            )}
            <Row label={t('cart.taxable')} paise={totals.taxableAmountPaise} />
            {totals.cgstAmountPaise > 0 && (
              <Row label="CGST" paise={totals.cgstAmountPaise} />
            )}
            {totals.sgstAmountPaise > 0 && (
              <Row label="SGST" paise={totals.sgstAmountPaise} />
            )}
            {totals.igstAmountPaise > 0 && (
              <Row label="IGST" paise={totals.igstAmountPaise} />
            )}
            {totals.cessAmountPaise > 0 && (
              <Row label={t('cart.cess')} paise={totals.cessAmountPaise} />
            )}
            {totals.roundOffPaise !== 0 && (
              <Row label={t('cart.roundOff')} paise={totals.roundOffPaise} />
            )}
            <hr className="divider" />
            <div className="row-between">
              <span className="t-bold">{t('cart.payable')}</span>
              <Money paise={totals.netAmountPaise} className="t-lg" />
            </div>
          </div>
        </Card>

        <div className="stack-2">
          {/*
            STAFF GO BACK TO THE CUSTOMER THEY ARE STANDING IN FRONT OF.

            Requested 2 Oct. A salesperson's next action after placing an order
            is almost never "track this order" — it is to look at the same
            shop's outstanding, or raise a second order, or move on. Sending
            him to the catalogue meant re-picking the customer every time.

            A retailer ordering for himself has no customer card to return to,
            so he keeps the original two buttons.
          */}
          {isStaff && customerId ? (
            <>
              <Button block onClick={() => navigate(`/customers/${customerId}`)}>
                Back to customer
              </Button>
              {/* Set a follow-up right after booking (item #5) — "when is the next
                  order likely?" is asked at the door, so the prompt is here. */}
              <Button variant="ghost" block onClick={() => setReminderOpen(true)}>
                Set a reminder
              </Button>
              <Button variant="ghost" block onClick={() => navigate(`/orders/${result.orderId}`)}>
                {t('order.trackOrder')}
              </Button>
              {reminderOpen && (
                <ReminderSheet
                  open={reminderOpen}
                  onClose={() => setReminderOpen(false)}
                  customerId={customerId}
                  orderId={result.orderId}
                  source="order"
                />
              )}
            </>
          ) : (
            <>
              <Button block onClick={() => navigate(`/orders/${result.orderId}`)}>
                {t('order.trackOrder')}
              </Button>
              <Button variant="ghost" block onClick={() => navigate('/catalogue')}>
                {t('nav.catalogue')}
              </Button>
            </>
          )}
        </div>
      </div>
    </Page>
  );
}

function Row({ label, paise, tone }: { label: string; paise: number; tone?: 'ok' }) {
  return (
    <div className="row-between t-sm">
      <span className="t-muted">{label}</span>
      <Money paise={paise} tone={tone} />
    </div>
  );
}
